import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarClock, Wallet, MessageSquare, User, Loader2, Power, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { DashboardShell, StatCard, StatusBadge } from "@/components/dashboard/DashboardShell";
import { api } from "@/lib/api";
import { gbp } from "@/lib/constants";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "bookings", label: "Jobs", icon: CalendarClock },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "profile", label: "Profile", icon: User },
];

const NEXT_ACTION = {
  driver_assigned: ["driver_accepted", "Accept Job"],
  driver_accepted: ["driver_enroute", "Start Journey to Pickup"],
  driver_enroute: ["driver_arrived", "Arrived"],
  driver_arrived: ["picked_up", "Passenger Picked Up"],
  picked_up: ["journey_started", "Start Journey"],
  journey_started: ["completed", "Complete Journey"],
};

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [data, setData] = useState(null);
  const [online, setOnline] = useState(false);

  const load = () => api.get("/driver/dashboard").then(({ data }) => { setData(data); setOnline(data.driver?.online); });
  useEffect(() => { load(); }, []);

  if (!data) return <DashboardShell title="Driver" nav={NAV} active={active} onNav={setActive}><div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div></DashboardShell>;

  const s = data.stats;
  const toggleOnline = async (v) => { setOnline(v); await api.post("/driver/online", { online: v }); toast.success(v ? "You're online" : "You're offline"); };
  const advance = async (b) => {
    const [next, ] = NEXT_ACTION[b.status] || [];
    if (!next) return;
    await api.patch(`/bookings/${b.id}/status`, { status: next });
    toast.success("Status updated");
    load();
  };
  const decline = async (b) => { await api.patch(`/bookings/${b.id}/status`, { status: "cancelled", note: "Declined by driver" }); toast.info("Job declined"); load(); };

  const jobs = data.bookings.filter((b) => !["completed", "cancelled", "no_show"].includes(b.status));
  const doneJobs = data.bookings.filter((b) => b.status === "completed");

  const JobCard = ({ b }) => {
    const next = NEXT_ACTION[b.status];
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`job-${b.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><span className="font-heading font-semibold text-navy">{b.booking_ref}</span><StatusBadge status={b.status} /></div>
            <p className="mt-1 flex items-center gap-1 text-sm text-slate-600"><MapPin size={13} className="text-brand" /> {b.pickup?.name}{b.dropoff ? ` → ${b.dropoff.name}` : ""}</p>
            <p className="mt-0.5 text-xs text-slate-400">{b.date} · {b.time} · {b.passengers?.adults} pax · {b.vehicle_label}</p>
            {b.notes && <p className="mt-1 text-xs italic text-slate-500">"{b.notes}"</p>}
          </div>
          <span className="font-heading text-lg font-bold text-navy">{gbp(b.total)}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {b.status === "driver_assigned" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => decline(b)} data-testid={`decline-${b.id}`}>Decline</Button>}
          {next && <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => advance(b)} data-testid={`advance-${b.id}`}>{next[1]}</Button>}
          <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate("/messages")}><MessageSquare size={13} /> Message</Button>
        </div>
      </div>
    );
  };

  return (
    <DashboardShell title="Driver" nav={NAV} active={active} onNav={(k) => k === "messages" ? navigate("/messages") : setActive(k)}>
      <div className="mb-6 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-3"><Power size={18} className={online ? "text-emerald-500" : "text-slate-400"} /><span className="font-medium text-navy">{online ? "Online — receiving jobs" : "Offline"}</span></div>
        <Switch checked={online} onCheckedChange={toggleOnline} data-testid="online-toggle" />
      </div>

      {active === "overview" && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={CalendarClock} label="Today's bookings" value={s.todays_bookings} />
            <StatCard icon={LayoutDashboard} label="Upcoming jobs" value={s.upcoming} color="navy" />
            <StatCard icon={Star} label="Rating" value={s.rating} color="amber" />
            <StatCard icon={Wallet} label="Total earnings" value={gbp(s.earnings_total)} color="emerald" />
          </div>
          <h2 className="font-heading text-lg font-bold text-navy">Active jobs</h2>
          <div className="grid gap-3">{jobs.length ? jobs.map((b) => <JobCard key={b.id} b={b} />) : <p className="text-sm text-slate-500">No active jobs right now.</p>}</div>
        </div>
      )}
      {active === "bookings" && <div className="grid gap-3">{data.bookings.map((b) => <JobCard key={b.id} b={b} />)}</div>}
      {active === "earnings" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Wallet} label="This week" value={gbp(s.earnings_week)} color="emerald" />
          <StatCard icon={Wallet} label="Total earnings" value={gbp(s.earnings_total)} color="navy" />
          <StatCard icon={Wallet} label="Pending payout" value={gbp(s.pending_payout)} color="amber" />
          <StatCard icon={Star} label="Completed jobs" value={doneJobs.length} />
        </div>
      )}
      {active === "profile" && data.driver && (
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6">
          <p className="font-heading text-lg font-semibold text-navy">{data.driver.name}</p>
          <p className="text-sm text-slate-500">{data.driver.operator_name}</p>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-500">City</span><span className="font-medium">{data.driver.city}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-500">Rating</span><span className="font-medium">{data.driver.rating} / 5</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-500">Trips</span><span className="font-medium">{data.driver.trips}</span></div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
